import 'abortcontroller-polyfill' // TODO do we still need this?
import '@/styles/globals.scss'
import '@fortawesome/fontawesome-svg-core/styles.css'
import { onAuthStateChanged } from '@firebase/auth'
import { config } from '@fortawesome/fontawesome-svg-core'
import { doc, onSnapshot, Unsubscribe } from 'firebase/firestore'
import { AppContext, AppProps } from 'next/app'
import { useRouter } from 'next/router'
import NextNProgress from 'nextjs-progressbar'
import { useEffect, useMemo } from 'react'
import { CookiesProvider, Cookies } from 'react-cookie'
import ReactGA from 'react-ga4'
import { Provider } from 'react-redux'
import { persistStore } from 'redux-persist'

import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary'
import Layout from '@/components/Layout/Layout'
import RegionDetector from '@/components/RegionDetector/RegionDetector'
import ThemeProvider from '@/components/ThemeProvider/ThemeProvider'
import CookieService from '@/services/cookie.service'
import { auth, firestore } from '@/services/firebase.service'
import { makeStore } from '@/store/store'
import { SET_USER_STATE } from '@/store/user/actions/types'
import { getUserInitialState } from '@/store/user/initialState'
import { FirestoreProfileType } from '@/types/firestore'

config.autoAddCss = false

let unsub: Unsubscribe | undefined

const ClientOnlyPersistGate = ({ children }: { children: React.ReactNode; persistor: any }) => {
  if (typeof window === undefined) return children
  return children
}

// The store requires a subobject reference so that a callback function doesn't get stale
const storeRef: { current: ReturnType<typeof makeStore> | null } = { current: null }
const MyApp = ({ Component, cookies, pageProps }: AppProps & { cookies: any }) => {
  const isBrowser = typeof window !== 'undefined'

  const initialCookies = !cookies ? undefined : new Cookies(cookies)

  const initialUserState = initialCookies ? initialCookies.get('userState') : {}

  const router = useRouter()

  const { store, persistor } = useMemo(() => {
    const newStore = makeStore({
      user: { ...getUserInitialState(), ...initialUserState },
    })
    return {
      store: newStore,
      persistor: persistStore(newStore),
    }
  }, [])
  storeRef.current = store

  useEffect(() => {
    onAuthStateChanged(auth, (user) => {
      if (user && !unsub) {
        unsub = onSnapshot(doc(firestore, 'users', user.uid), (doc) => {
          const data = doc.data() as FirestoreProfileType

          // Use subobject reference to avoid stale callback
          storeRef.current?.dispatch({
            type: SET_USER_STATE,
            payload: {
              uid: user.uid,
              ...data,
            },
          })
        })
      } else if (unsub) {
        unsub()
      }
    })

    // React GA4
    ReactGA.initialize(process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID as string)
    const handleRouteChange = (url: string) => {
      ReactGA.send({ hitType: 'pageview', page: url })
    }

    router.events.on('routeChangeComplete', handleRouteChange)

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [])

  return (
    <ErrorBoundary>
      <CookiesProvider cookies={isBrowser ? undefined : new Cookies(cookies)}>
        <ThemeProvider initial={cookies ? cookies.theme === 'true' : CookieService.get('theme') === 'true'}>
          <Provider store={store}>
            <ClientOnlyPersistGate persistor={persistor}>
              <NextNProgress />
              <RegionDetector />
              <Layout persistor={persistor}>
                <Component {...pageProps} />
              </Layout>
            </ClientOnlyPersistGate>
          </Provider>
        </ThemeProvider>
      </CookiesProvider>
    </ErrorBoundary>
  )
}

MyApp.getInitialProps = async (context: AppContext): Promise<any> => {
  return { cookies: context.ctx.req?.headers?.cookie }
}

export default MyApp
